<template>
<v-content class="bg-content-coach" :class="themeClass">

    <div class="coach-wrap">
      <!-- Top bar -->
      <header class="coach-header">
        <div class="brand">
          <div class="brand-mark" aria-hidden="true">
            <MotivationAvatar
  ref="avatarSm"
  :recognizing="recognizing"
  :speaking="ttsSpeaking"
  :thinking="loading"
  :celebrate="showCelebrate"
  :mood="avatarMood"
  :energy="waveLevel"
  :caption="avatarCaption"
  :level="level"
  @theme="onAvatarTheme"
  @trophy="onAvatarTrophy"
  class="avatar"
/></div>
<v-divider vertical class="mx-3"></v-divider>

          <div>
            <div class="title">Hatch</div>
            <div class="subtitle">Personal AI Coach</div>
          </div>
        </div>
<!-- STATUS SUMMARY -->
<section class="card neon status-card">
  <div class="section-head">
    <h3>📊 Status & Next Steps</h3>
  </div>

  <div class="status-counters">
    <v-chip small>Goals: {{ goals.length }} ({{ goalsActive }} active, {{ goalsCompleted }} done)</v-chip>
    <v-chip small>Quick tasks: {{ quickTasks.length }} ({{ tasksActive }} active, {{ tasksDone }} done)</v-chip>
    <v-chip small v-if="activeGoal">Path: {{ completedCount }} / {{ totalCount }} steps</v-chip>
  </div>

  <div class="status-brief">
    <strong>Next:</strong> {{ nextStepBrief }}
    <v-btn
      x-small
      class="ml-2"
      color="success"
      v-if="hasNextStep"
      @click="runNextStepAction"
    >Do it</v-btn>
  </div>
</section>
        <div class="right-tools">
          <div class="points-pill" :title="`XP: ${xp}`">
            <span>XP</span>
            <strong>{{ xp }}</strong>
          </div>

          <div class="streak-pill" :title="`Daily streak: ${streak} day(s)`">
            🔥 <strong>{{ streak }}</strong>
          </div>

          <div class="level-pill">
            <span>Level</span>
            <strong>{{ level }}</strong>
          </div>

          <div class="tog" title="Toggle dark mode">
            <div>Dark</div>
            <div class="switch" :class="{ on: isDark }" role="switch" :aria-checked="String(isDark)" tabindex="0"
                 @click="toggleTheme" @keydown.enter.prevent="toggleTheme" @keydown.space.prevent="toggleTheme"></div>
          </div>
        </div>
        <v-btn small outlined class="ml-2" @click="openIntakeDialog" title="Edit profile">
  <v-icon left small>mdi-account</v-icon> Profile
</v-btn>

      </header>

      <!-- Mic / Voice visualizer -->
      <section class="voice-row">
        <div class="avatar-container">
<MotivationAvatarLG
 ref="avatarLG"
  :recognizing="recognizing"
  :speaking="ttsSpeaking"
  :thinking="loading"
  :celebrate="showCelebrate"
  :mood="avatarMood"
  :energy="waveLevel"
  :caption="avatarCaption"
  :level="level"
  :question="lastTranscript"
  :answer="lastAnswer"
  :command="avatarCmd"       
  :xp="xpPulse"             
  @intent="onAvatarIntent"       
  @theme="onAvatarTheme"
  @trophy="onAvatarTrophy"
  @fx="onAvatarFx"
  @ack="onAvatarAck"
/>


          <div class="pulse-glow" :class="{ thinking: loading || recognizing }"></div>
          <div class="mic-wrap">
            <button class="mic-btn" :class="{ on: recognizing, disabled: !voiceSupported }"
                    :aria-pressed="recognizing ? 'true' : 'false'"
                    :title="voiceSupported ? (recognizing ? 'Stop voice (Space)' : 'Start voice (Space)') : 'Voice not supported'"
                    @click="toggleVoice">
              <v-icon v-if="!recognizing" small>mdi-microphone</v-icon>
              <v-icon v-else small>mdi-stop-circle</v-icon>
            </button>
            <div class="voice-ring" :class="{ on: recognizing }" ref="voiceRing"></div>
          </div>
          <canvas ref="vizCanvas" class="voice-viz" :class="{ on: recognizing || loading || ttsSpeaking }" aria-hidden="true"></canvas>
        </div>
      <div class="voice-cta-wrap">
  <v-btn
    class="voice-cta"
    :color="recognizing ? 'red darken-1' : 'teal lighten-1'"
    :disabled="!voiceSupported"
    @click="toggleVoice"
  >
    <v-icon left>{{ recognizing ? 'mdi-stop-circle' : 'mdi-microphone' }}</v-icon>
    {{ recognizing ? 'Stop Listening' : 'Start Speaking' }}
  </v-btn>

  <small v-if="voiceSupported" class="voice-hint">
    Tip: press <kbd>Space</kbd> to {{ recognizing ? 'stop' : 'start' }}.
  </small>
  <small v-else class="voice-hint warn">
    Your browser doesn't support voice input. Try Chrome.
  </small>

<!-- Quick command cheats -->
<div class="voice-cheats" style="margin-top:8px;">
  <v-chip x-small outlined class="mr-1">say “create task …”</v-chip>
  <v-chip x-small outlined class="mr-1">say “start new goal …”</v-chip>
  <v-chip x-small outlined class="mr-1">say “save”</v-chip>
  <v-chip x-small outlined>say “cancel”</v-chip>
  <v-chip x-small outlined class="mr-1">say “open intake”</v-chip>

</div>


  <!-- Global capture banner -->
  <v-alert
    v-if="capture.active"
    type="info"
    dense
    outlined
    class="mt-2"
    border="left"
  >
    🎙️ Voice capture is on —
    <strong>{{ capture.target }}</strong>
    <span v-if="capture.field">• {{ capture.field }}</span>.
    <span class="muted">Prompt: “{{ capture.prompt }}” • You can say “save” or “cancel”.</span>
  </v-alert>
</div>

      </section>
      <br />
<!-- User input row -->
<section class="input-row card neon">
  <v-form @submit.prevent="submitTyped">
    <v-text-field
      v-model="typedQuestion"
      label="Type your question"
      outlined
      dense
      hide-details
      clearable
    ></v-text-field>
    <v-btn color="primary" type="submit">Ask</v-btn>
  </v-form>
</section>

<!-- Transcript display -->
<section class="transcript-row card" aria-live="polite" aria-atomic="true">
  <h4>🗣️ Live Transcript</h4>
  <div class="transcript-box">
    <div v-if="lastTranscript" class="transcript-line">
      <strong>You:</strong> {{ lastTranscript }}
    </div>
    <div v-else class="muted">No speech detected yet.</div>
  </div>
</section>


      <!-- Intake -->
      <v-dialog v-model="showIntake" max-width="760" persistent>
        <v-card class="neon">
          <v-card-title class="headline">Welcome! Let’s set up your coach profile</v-card-title>
          <v-card-text>
            <v-form ref="intakeForm" v-model="intakeValid">
              <v-text-field v-model="profile.name" label="Your name" :rules="[v=>!!v||'Required']" outlined dense/>
              <v-text-field v-model="profile.focusArea" label="Main focus right now (e.g., Fitness, Learning, Habit)" outlined dense/>
              <v-select v-model="profile.preferredStyle" :items="coachStyles" label="Coaching style" outlined dense/>
              <v-select v-model="profile.voice" :items="voicesList" label="Preferred voice" outlined dense/>
              <v-text-field v-model="profile.dailyTime" label="Daily time you can commit (e.g., 25 min)" outlined dense/>
              <v-textarea v-model="profile.context" label="Anything I should know (injuries, schedule, constraints)" outlined auto-grow rows="2"/>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn color="primary" :disabled="!intakeValid" @click="saveIntake">Save & Continue</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>



      <!-- Main coach grid -->
<main class="grid-areas">
  <!-- LEFT: Goals -->
  <section class="col-left card neon">
    <!-- (unchanged) Goals panel content -->
    <div class="section-head">
      <h3>🎯 Your Goals</h3>
      <div class="actions">
        <v-btn small outlined color="primary" @click="promptNewGoal">New Goal</v-btn>
        <v-btn small @click="dailyCheckIn">Daily Check-in</v-btn>
      </div>
    </div>

    <div class="goal-list" v-if="goals.length">
      <div v-for="g in goals" :key="g.id" class="goal-item" :class="{ active: activeGoal && activeGoal.id===g.id }">
        <div class="gi-main">
          <div class="gi-title">{{ g.title }}</div>
          <div class="gi-sub">{{ g.outcome }}</div>
        </div>
        <div class="gi-meta">
          <v-chip x-small>{{ g.category }}</v-chip>
          <v-chip x-small>{{ g.priority }}</v-chip>
          <v-chip x-small :color="g.status==='active' ? 'green' : 'grey'" dark>{{ g.status }}</v-chip>
        </div>
        <div class="gi-actions">
          <v-btn x-small @click="selectGoal(g)">Open</v-btn>
          <v-btn x-small outlined color="primary" @click="generatePathway(g)" :loading="planningId===g.id">Generate Pathway</v-btn>
          <v-btn x-small text color="red" @click="deleteGoal(g.id)">Delete</v-btn>
        </div>
      </div>
    </div>
    <div v-else class="muted">No goals yet. Click <em>New Goal</em> to get started.</div>
  </section>

  <!-- CENTER: Voice + Input + Transcript -->
  <section class="col-center">
    <!-- <section class="card neon center-card">

      <section class="voice-row">
        <div class="avatar-container">
          <div class="pulse-glow" :class="{ thinking: loading || recognizing }"></div>
          <div class="mic-wrap">
            <button class="mic-btn" :class="{ on: recognizing, disabled: !voiceSupported }"
                    :aria-pressed="recognizing ? 'true' : 'false'"
                    :title="voiceSupported ? (recognizing ? 'Stop voice (Space)' : 'Start voice (Space)') : 'Voice not supported'"
                    @click="toggleVoice">
              <v-icon v-if="!recognizing" small>mdi-microphone</v-icon>
              <v-icon v-else small>mdi-stop-circle</v-icon>
            </button>
            <div class="voice-ring" :class="{ on: recognizing }" ref="voiceRing"></div>
          </div>
          <canvas ref="vizCanvas" class="voice-viz" :class="{ on: recognizing || loading || ttsSpeaking }" aria-hidden="true"></canvas>
        </div>
        <div class="voice-cta-wrap">
          <v-btn class="voice-cta" :color="recognizing ? 'red darken-1' : 'teal lighten-1'" :disabled="!voiceSupported" @click="toggleVoice">
            <v-icon left>{{ recognizing ? 'mdi-stop-circle' : 'mdi-microphone' }}</v-icon>
            {{ recognizing ? 'Stop Listening' : 'Start Speaking' }}
          </v-btn>
          <small v-if="voiceSupported" class="voice-hint">Tip: press <kbd>Space</kbd> to {{ recognizing ? 'stop' : 'start' }}.</small>
          <small v-else class="voice-hint warn">Your browser doesn't support voice input. Try Chrome.</small>
        </div>
      </section>
    </section> -->

    <!-- typed input -->
    <!-- <section class="input-row card neon">
      <v-form @submit.prevent="submitTyped">
        <v-text-field v-model="typedQuestion" label="Type your question" outlined dense hide-details clearable />
        <v-btn color="primary" type="submit">Ask</v-btn>
      </v-form>
    </section> -->

    <!-- transcript -->
    <!-- <section class="transcript-row card">
      <h4>🗣️ Live Transcript</h4>
      <div class="transcript-box">
        <div v-if="lastTranscript" class="transcript-line">
          <strong>You:</strong> {{ lastTranscript }}
        </div>
        <div v-else class="muted">No speech detected yet.</div>
      </div>
    </section> -->
    <!-- QUICK TASKS PANEL -->
<section class="card neon tasks-card">
  <div class="section-head">
    <h3>✅ Quick Tasks</h3>
    <div class="actions">
      <v-btn small outlined color="primary" @click="openTaskDialog()">New Task</v-btn>
    </div>
  </div>

  <div v-if="quickTasks.length" class="task-list">
    <div v-for="t in quickTasks" :key="t.id" class="qtask" :class="{ done: t.status==='done', active: t.status==='active' }">
      <div class="qtask-main">
        <div class="qtask-title">{{ t.title }}</div>
        <div class="qtask-sub muted">
          <span v-if="t.due">Due: {{ formatDate(t.due) }}</span>
          <span v-if="t.notes"> • {{ t.notes }}</span>
        </div>
      </div>
      <div class="qtask-meta">
        <v-chip x-small :color="chipColor(t.status)" dark>{{ t.status }}</v-chip>
      </div>
      <div class="qtask-actions">
        <v-btn x-small text @click="startQuickTask(t)">Start</v-btn>
        <v-btn x-small text color="success" @click="completeQuickTask(t)" :disabled="t.status==='done'">Done</v-btn>
        <v-btn x-small outlined color="primary" @click="openTaskDialog(t)">Edit</v-btn>
        <v-btn x-small text color="red" @click="deleteQuickTask(t.id)">Delete</v-btn>
      </div>
    </div>
  </div>
  <div v-else class="muted">No quick tasks yet. Say <em>“add task …”</em> or click <em>New Task</em>.</div>
</section>

  </section>


  <!-- RIGHT: Pathway -->
  <section class="col-right card neon" v-if="activeGoal">
    <!-- (unchanged) pathway content -->
    <div class="section-head">
      <h3>🗺️ Pathway — {{ activeGoal.title }}</h3>
      <div class="actions">
        <v-btn small outlined @click="coachTip">Get a Hint</v-btn>
        <v-btn small color="success" :disabled="!isPathComplete" @click="completeGoal">Complete Goal</v-btn>
      </div>
    </div>

    <div class="progress-row" v-if="activeGoal.path && activeGoal.path.milestones.length">
      <div class="pr-text">Progress</div>
      <v-progress-linear :value="pathProgress" height="10" rounded />
      <div class="pr-num">{{ completedCount }} / {{ totalCount }} steps</div>
    </div>

    <div class="milestone" v-for="(m, mi) in activeGoal.path.milestones" :key="m.id">
      <div class="ms-head">
        <div class="ms-title">{{ m.title }}</div>
        <div class="ms-meta">
          <v-chip x-small>{{ m.duration || '—' }}</v-chip>
          <v-chip x-small>{{ m.when || 'Anytime' }}</v-chip>
        </div>
      </div>
      <div class="task" v-for="(t, ti) in m.tasks" :key="t.id" :class="{ done: t.done }">
        <v-checkbox hide-details dense :input-value="t.done" @change="toggleTask(mi, ti)" :label="t.title" />
<div class="task-actions">
  <v-btn x-small text @click="expandTask(mi, ti)">Details</v-btn>
  <v-btn x-small outlined color="primary" @click="startTask(mi, ti)">Start</v-btn>
  <v-btn x-small text color="success" @click="completeMilestoneTask(mi, ti)" :disabled="t.done">Complete</v-btn>
</div>

        <div class="task-details" v-if="t.show">
          <div class="muted" v-html="t.detail || 'Breakdown coming soon…'"></div>
          <div class="mini-controls">
            <v-btn x-small @click="askCoachFor(t)">Coach me</v-btn>
            <v-btn x-small text @click="addNote(mi, ti)">Add note</v-btn>
          </div>
          <div class="note" v-if="t.note">
            <strong>Note:</strong> {{ t.note }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="!activeGoal.path || !activeGoal.path.milestones.length" class="muted">
      No pathway yet. Click <em>Generate Pathway</em> to create structured steps.
    </div>
  </section>
</main>

      <!-- New goal dialog -->
<!-- New goal dialog -->
<v-dialog v-model="goalDialog" max-width="720">
  <v-card class="neon">
    <v-card-title class="headline">Create a personal goal</v-card-title>
    <v-card-text>
      <v-form ref="goalForm" v-model="goalValid">
        <v-text-field
          v-model="newGoal.title"
          label="Goal title (e.g., Run a 5K)"
          :rules="[v=>!!v||'Required']"
          outlined
          dense
        />
        <v-textarea
          v-model="newGoal.outcome"
          label="Why this matters / outcome"
          outlined
          rows="2"
          auto-grow
        />
        <v-select
          v-model="newGoal.category"
          :items="goalCategories"
          label="Category"
          outlined
          dense
        />
        <v-select
          v-model="newGoal.priority"
          :items="['Low','Medium','High']"
          label="Priority"
          outlined
          dense
        />
        <v-menu
          ref="dueMenu"
          v-model="dueMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="newGoal.due"
              label="Target date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              outlined
              dense
            />
          </template>
          <v-date-picker
            v-model="newGoal.due"
            no-title
            scrollable
            @input="dueMenu=false"
          />
        </v-menu>
      </v-form>

      <!-- Voice capture hint (for goal) -->
      <div
        v-if="capture.active && capture.target==='goal'"
        class="muted"
        style="margin-top:8px;"
      >
        🎙️ Voice capture: {{ capture.prompt }}
      </div>
    </v-card-text>
    <v-card-actions>
      <div class="muted mr-3">Tip: you can say “save” or “cancel”.</div>
      <v-spacer/>
      <v-btn text @click="goalDialog=false">Cancel</v-btn>
      <v-btn color="primary" :disabled="!goalValid" @click="createGoal">Create</v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>

      <!-- Task dialog (voice-prefill aware) -->
<v-dialog v-model="taskDialog" max-width="640">
  <v-card class="neon">
    <v-card-title class="headline">{{ taskForm.id ? 'Edit Task' : 'New Task' }}</v-card-title>
    <v-card-text>
      <v-form ref="taskFormRef" v-model="taskValid">
        <v-text-field v-model="taskForm.title" :rules="[v=>!!v||'Required']" label="Task title" outlined dense autofocus/>
        <v-menu ref="taskDueMenu" v-model="taskDueMenu" :close-on-content-click="false" transition="scale-transition" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="taskForm.due" label="Due date" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" outlined dense/>
          </template>
          <v-date-picker v-model="taskForm.due" no-title scrollable @input="taskDueMenu=false"></v-date-picker>
        </v-menu>
        <v-textarea v-model="taskForm.notes" label="Notes" outlined rows="2" auto-grow/>
        <v-select v-model="taskForm.status" :items="taskStatuses" label="Status" outlined dense/>
      </v-form>
      <div v-if="capture.active" class="muted" style="margin-top:8px;">
        🎙️ Voice capture: {{ capture.prompt }}
      </div>
    </v-card-text>
<v-card-actions>
  <div class="muted mr-3">Tip: you can say “save” or “cancel”.</div>
  <v-spacer/>
  <v-btn v-if="taskForm.id" text color="red" @click="deleteQuickTask(taskForm.id); taskDialog=false">Delete</v-btn>
  <v-btn text @click="taskDialog=false">Cancel</v-btn>
  <v-btn color="primary" :disabled="!taskValid" @click="saveTask">Save</v-btn>
</v-card-actions>

  </v-card>
</v-dialog>



      <!-- Celebration overlay -->
      <div class="celebrate" v-if="showCelebrate">
        <canvas ref="confetti" class="confetti"></canvas>
        <div class="celebrate-card">
          <div class="big">🎉</div>
          <h2>Goal completed!</h2>
          <p>Fantastic job, {{ profile.name || 'champion' }} — enjoy your XP boost!</p>
          <v-btn color="primary" @click="endCelebration">Continue</v-btn>
        </div>
      </div>

      <!-- Snackbar / toasts -->
      <v-snackbar v-model="snack.on" :timeout="2500">{{ snack.msg }}</v-snackbar>
    </div>
    <br />
  </v-content>
</template>

<script>
// Drop-in Vue 2 component that reuses your existing voice/TTS helpers pattern.
// It adds: intake, goals, AI-generated pathways, XP/levels/streaks, and a celebration overlay.
// It expects Vuetify present (matching your project). No server changes are strictly required.
import MotivationAvatar from '@/components/MotivationAvatar.vue';
import MotivationAvatarLG from '../components/MotivationAvatarLG.vue';
import axios from 'axios';

export default {
  name: 'AICoachGamified',
  components: {MotivationAvatar, MotivationAvatarLG },
  data() {
    return {
      // THEME
      isDark: true,
      avatarMood: 'friendly',
      avatarCaption: '',
      // Busy flags
      loading: false,
      planningId: null,

      // Intake
      showIntake: false,
      intakeValid: false,
      typedQuestion: '',
    lastTranscript: '',
      coachStyles: ['Direct & pragmatic', 'Supportive & encouraging', 'Data-driven & analytical', 'Playful & gamified'],
      voicesList: ['Google UK English Female', 'Google US English', 'Serena (en-GB)', 'Samantha (en-US)'],
      profile: {
        name: '',
        focusArea: '',
        preferredStyle: 'Supportive & encouraging',
        voice: 'Google UK English Female',
        dailyTime: '25 min',
        context: '',
      },

      // Goals
      goals: [],
      activeGoal: null,
      goalDialog: false,
      goalValid: false,
      newGoal: { title: '', outcome: '', category: 'Fitness', priority: 'Medium', due: null },
      goalCategories: ['Fitness', 'Learning', 'Career', 'Finance', 'Wellness', 'Habit', 'Home', 'Creative'],
      dueMenu: false,

      // Gamification
      xp: 0,
      level: 1,
      streak: 0,
      lastCheckIn: null,
      badges: [],

      // Celebrations
      showCelebrate: false,
      confettiRAF: 0,
      confettiResize: null,

      // Voice
      voiceSupported: false,
      recognizing: false,
      recognition: null,
      ttsSpeaking: false,
      resumeAfterSpeak: false,

    // typedQuestion: '',
    // lastTranscript: '',

// Voice → Avatar energy bridge
waveLevel: 0.18,

// Quick tasks (already present in your file; keep)
quickTasks: [],
taskDialog: false,
taskValid: false,
taskForm: { id: null, title: '', due: null, notes: '', status: 'todo' },
taskStatuses: ['todo','active','done'],
taskDueMenu: false,

// Voice capture flow for forms (task or goal)
capture: { active: false, target: null, field: null, prompt: '' },
    // Visualizer (modern)
    viz: {
      ctx: null,
      dpr: (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1,
      raf: 0,
      audioCtx: null,
      analyser: null,
      stream: null,
      buf: null,
      energy: 0,
    },

      // Snackbar
      snack: { on: false, msg: '' },

      // GPT config — DO NOT change connection pattern
      gptModel: 'gpt-4o',
 gptApiKey: "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", // Replace with your OpenAI API key
    };
  },
  computed: {
    themeClass() { return this.isDark ? 'theme--dark' : 'theme--light'; },
 totalCount() {
      if (!this.activeGoal || !this.activeGoal.path) return 0;
      return this.activeGoal.path.milestones.reduce((n, m) => n + (m.tasks?.length || 0), 0);
    },
    completedCount() {
      if (!this.activeGoal || !this.activeGoal.path) return 0;
      return this.activeGoal.path.milestones.reduce((n, m) => n + (m.tasks?.filter(t => t.done).length || 0), 0);
    },
    pathProgress() {
      return this.totalCount ? Math.round((100 * this.completedCount) / this.totalCount) : 0;
    },
    isPathComplete() {
      return this.totalCount > 0 && this.completedCount === this.totalCount;
    },
    goalsActive() { return this.goals.filter(g => g.status === 'active').length; },
goalsCompleted() { return this.goals.filter(g => g.status === 'completed').length; },
tasksActive() { return this.quickTasks.filter(t => t.status === 'active').length; },
tasksDone() { return this.quickTasks.filter(t => t.status === 'done').length; },

// Next-step chooser used by the Status card
nextStepInfo() {
  // Path step first (if any)
  const step = this.getNextPathStep();
  if (step) {
    return {
      type: 'path',
      brief: `Path step — ${step.title}`,
      mi: step.mi,
      ti: step.ti,
    };
  }
  // Otherwise next quick task (active first, else soonest due todo)
  const qt = this.getNextQuickTask();
  if (qt) {
    return {
      type: 'qtask',
      brief: `Quick task — ${qt.title}`,
      id: qt.id,
    };
  }
  return null;
},
nextStepBrief() {
  return this.nextStepInfo
    ? this.nextStepInfo.brief
    : 'No pending items — create a task or generate a pathway.';
},
hasNextStep() { return !!this.nextStepInfo; },

  },
   watch: {
    // set mood from app events
    pathProgress(p){ if (p >= 90) this.avatarMood = 'proud'; },
    recognizing(n){ if (n) this.avatarMood = 'focused'; }
  },
  mounted() {
 this.loadState();
  if (!this.profile.name) this.showIntake = true;

  this.setupSpeech();
  this.initTTS();
  this.initVoiceViz(); // new: sets up canvas & idle animation

  window.addEventListener('keydown', this.onKey);
  },
  beforeDestroy() {
window.removeEventListener('keydown', this.onKey);
  this.stopConfetti();
  try { if (this.recognition) this.recognition.stop(); } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.debug('SpeechRecognition.stop() error:', err);
  }
try { this.viz.audioCtx?.close?.(); } catch (_) { void 0; }
  this.stopMicStream();
  this.teardownViz();
  },
  methods: {
initTTS() {
  const s = window.speechSynthesis;
  if (!s) return;
  s.getVoices(); // warm
  if ('onvoiceschanged' in s) {
    s.onvoiceschanged = () => { /* noop, forces population */ s.getVoices(); };
  }
},
  avatarRef() {
    return this.$refs.avatarLg || this.$refs.avatarSm || null;
  },
/** Parse flexible date phrases: "tomorrow", "Sep 15", "next Tuesday", "in 3 days", "in 2 weeks" */
parseDateNL(input, base = new Date()) {
  if (!input) return null;
  const s = String(input).trim().toLowerCase();

  // Absolute-like: let Date parse first
  let dt = new Date(input);
  if (!isNaN(dt.getTime())) return this._isoDate(dt);

  // Keywords
  if (s === 'today') return this._isoDate(base);
  if (s === 'tomorrow') return this._isoDate(this._addDays(base, 1));

  // in N days/weeks/months
  let m = s.match(/^in\s+(\d+)\s*(day|days|week|weeks|month|months)\b/);
  if (m) {
    const n = parseInt(m[1], 10);
    const unit = m[2];
    if (/day/.test(unit)) return this._isoDate(this._addDays(base, n));
    if (/week/.test(unit)) return this._isoDate(this._addDays(base, n * 7));
    if (/month/.test(unit)) { const d = new Date(base); d.setMonth(d.getMonth() + n); return this._isoDate(d); }
  }

  // next weekday
  m = s.match(/^next\s+(mon|tue|tues|weds?|wed|thu|thur|thurs|fri|sat|sun|sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/);
  if (m) return this._isoDate(this._nextWeekday(base, m[1]));

  // "on the 15th", "the 3rd" (assume this month)
  m = s.match(/(?:on\s+)?the\s+(\d{1,2})(st|nd|rd|th)?/);
  if (m) {
    const d = new Date(base);
    d.setDate(parseInt(m[1], 10));
    return this._isoDate(d);
  }

  // Try adding current year for "September 15th"
  m = s.match(/^(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\s+\d{1,2}(st|nd|rd|th)?$/);
  if (m) {
    const year = base.getFullYear();
    dt = new Date(`${input} ${year}`);
    if (!isNaN(dt.getTime())) {
      // If parsed date is in the past, roll to next year
      if (dt < base) dt.setFullYear(year + 1);
      return this._isoDate(dt);
    }
  }

  // Last-chance MM/DD or M/D, add current year
m = s.match(/^(\d{1,2})[/-](\d{1,2})$/);
if (m) {
  const mm = parseInt(m[1],10), dd = parseInt(m[2],10);
  dt = new Date(base.getFullYear(), mm - 1, dd);
  if (!isNaN(dt.getTime())) {
    if (dt < base) dt.setFullYear(dt.getFullYear() + 1);
    return this._isoDate(dt);
  }
}


  return null;
},
_addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; },
_isoDate(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0,10); },
_nextWeekday(base, wd) {
  const map = {sun:0, sunday:0, mon:1, monday:1, tue:2, tues:2, tuesday:2, wed:3, weds:3, wednesday:3, thu:4, thur:4, thurs:4, thursday:4, fri:5, friday:5, sat:6, saturday:6};
  const target = map[wd.toLowerCase()];
  if (target == null) return null;
  const d = new Date(base);
  const delta = (7 + target - d.getDay()) % 7 || 7;
  d.setDate(d.getDate() + delta);
  return d;
},
openIntakeDialog() {
  this.showIntake = true;
  this.$nextTick(() => this.$refs.nameField?.focus?.());
},

// Mark a pathway task complete (with XP + avatar cheer)
completeMilestoneTask(mi, ti) {
  const t = this.activeGoal?.path?.milestones?.[mi]?.tasks?.[ti];
  if (!t || t.done) return;
  t.done = true;
  this.grantXP(25);
  this.beep();
  this.avatarRef()?.celebrateTask(25, 'Step complete!'); // requires your upgraded MotivationAvatar
  this.saveState();
},

// Complete quick task by id (used by Status card)
completeQuickTaskById(id) {
  const t = this.quickTasks.find(x => x.id === id);
  if (!t || t.status === 'done') return;
  this.completeQuickTask(t);
},

// Find next path step (first not-done)
getNextPathStep() {
  if (!this.activeGoal?.path?.milestones) return null;
  for (let mi = 0; mi < this.activeGoal.path.milestones.length; mi++) {
    const m = this.activeGoal.path.milestones[mi];
    const ti = (m.tasks || []).findIndex(x => !x.done);
    if (ti >= 0) return { mi, ti, title: m.tasks[ti].title };
  }
  return null;
},

// Find the next quick task to suggest (active first; else nearest due todo)
getNextQuickTask() {
  const active = this.quickTasks.find(t => t.status === 'active');
  if (active) return active;
  const todos = this.quickTasks
    .filter(t => t.status === 'todo')
    .sort((a,b) => new Date(a.due || 8640000000000000) - new Date(b.due || 8640000000000000));
  return todos[0] || null;
},

// Wire the “Do it” button to the chosen next step
runNextStepAction() {
  const info = this.nextStepInfo;
  if (!info) return;
  if (info.type === 'path') this.completeMilestoneTask(info.mi, info.ti);
  if (info.type === 'qtask') this.completeQuickTaskById(info.id);
},
trySaveCurrentForm() {
  // Task
  if (this.taskDialog) {
    if (!this.taskForm.title) { this.speak('Please give the task a title first.'); return; }
    this.saveTask();
    this.speak('Task saved.');
    return;
  }
  // Goal
  if (this.goalDialog) {
    const ok = this.$refs.goalForm ? this.$refs.goalForm.validate() : !!this.newGoal.title;
    if (!ok) { this.speak('Please add a goal title.'); return; }
    this.createGoal();
    this.speak('Goal saved.');
    return;
  }
  // Intake/Profile
  if (this.showIntake) {
    const ok = this.$refs.intakeForm ? this.$refs.intakeForm.validate() : !!this.profile.name;
    if (!ok) { this.speak('Please enter your name before saving.'); return; }
    this.saveIntake();
    this.speak('Profile saved.');
  }
},

tryCancelCurrentForm() {
  if (this.taskDialog) {
    this.taskDialog = false;
    this.capture = { active:false, target:null, field:null, prompt:'' };
    this.speak('Cancelled.');
    return;
  }
  if (this.goalDialog) {
    this.goalDialog = false;
    this.capture = { active:false, target:null, field:null, prompt:'' };
    this.speak('Cancelled.');
  }
},

onAvatarTheme({ level, theme }) {
  const root = document.documentElement;
  root.style.setProperty('--accent-a', theme.a);
  root.style.setProperty('--accent-b', theme.b);

  // use `level` too (and expose it to CSS if you want to style by level)
  root.dataset.level = String(level);
},

onAvatarTrophy({ kind }) {
  // record trophy so `kind` is used (and you can show a history)
  this.badges.push({ kind, ts: Date.now() });
},
 toggleTheme() {
      this.isDark = !this.isDark;
try {
  localStorage.setItem('coach.dark', JSON.stringify(this.isDark));
} catch (err) {
  // no-op to satisfy ESLint
  void 0;
}
    },
 async submitTyped() {
    if (!this.typedQuestion.trim()) return;
    const q = this.typedQuestion.trim();
    this.lastTranscript = q;  // show in transcript panel too
    this.typedQuestion = '';
    await this.respondToUtterance(q);  // reuse same logic as voice
  },

onTranscript(text) {
  this.lastTranscript = text;

  const raw = String(text || '').trim();
  const s = raw.toLowerCase();

  const say = (m) => { this.toast(m); this.speak(m); };
  const isSave = /^(ok(ay)?\s+)?save(\s+(it|this|that|task|goal))?$|^(done|finish(ed)?|that's it|that is it|looks good|good to go)$/i.test(s);
  const isCancel = /^(cancel|close|dismiss|nevermind|never mind|stop)$/i.test(s);

  // ========== If we're currently capturing a form via voice ==========
  if (this.capture.active) {
    // ----- Common: allow "cancel" no matter which form -----
    if (isCancel) {
      if (this.capture.target === 'task') this.taskDialog = false;
      if (this.capture.target === 'goal') this.goalDialog = false;
      this.capture = { active:false, target:null, field:null, prompt:'' };
      say('Okay, canceled.');
      return;
    }

    // ----- TASK FORM VOICE FLOW -----
    if (this.capture.target === 'task') {
      // "save" while task dialog is open
      if (isSave) {
        if (!this.taskForm.title) {
          say('Please give the task a title before saving.');
          this.capture = { active:true, target:'task', field:'title', prompt:'What is the task title?' };
          return;
        }
        this.saveTask();
        say('Task saved.');
        return;
      }

      let m;

      // Title
      if ((m = raw.match(/^(?:title|set\s+title(?:\s+to)?|call\s+it)\s*(?::|-)?\s*(.+)$/i))) {
        this.taskForm.title = m[1].trim();
        say('Title set.');
        // If due is missing, ask for it
        if (!this.taskForm.due) {
          this.capture = { active:true, target:'task', field:'due', prompt:'When is it due?' };
          this.speak('When is it due?');
        } else {
          this.taskDialog = true;
          this.capture = { active:false, target:null, field:null, prompt:'' };
        }
        return;
      }

      // Due (natural: "due …", "by …", "on …", or "set due …")
      if ((m = raw.match(/^(?:due|set\s+due(?:\s+to)?|by|on)\s+(.+)$/i))) {
        const iso = this.parseDateNL(m[1].trim());
        if (iso) {
          this.taskForm.due = iso;
          say('Due date set.');
          this.taskDialog = true;
          this.capture = { active:false, target:null, field:null, prompt:'' };
        } else {
          say('I did not catch the date. Try: tomorrow, next Tuesday, or September 15th.');
        }
        return;
      }

      // Clear due
      if (/^clear\s+due$/i.test(s)) {
        this.taskForm.due = null;
        say('Due date cleared.');
        return;
      }

      // Notes
      if ((m = raw.match(/^(?:note|notes?)\s*(?::|-)?\s*(.+)$/i)) || (m = raw.match(/^add\s+note\s+(.+)$/i))) {
        const val = m[1].trim();
        this.taskForm.notes = this.taskForm.notes ? `${this.taskForm.notes}\n${val}` : val;
        say('Noted.');
        this.taskDialog = true;
        return;
      }

      // Status
      if ((m = raw.match(/^set\s+status(?:\s+to)?\s+(todo|active|done)$/i))) {
        this.taskForm.status = m[1].toLowerCase();
        say(`Status set to ${this.taskForm.status}.`);
        this.taskDialog = true;
        return;
      }

      // Fallback: if we were explicitly asking for a field, accept freeform
      if (this.capture.field === 'title') {
        this.taskForm.title = raw;
        if (!this.taskForm.due) {
          this.capture = { active:true, target:'task', field:'due', prompt:'When is it due?' };
          say('Got it. When is it due?');
        } else {
          this.taskDialog = true;
          this.capture = { active:false, target:null, field:null, prompt:'' };
          say('Title set. You can save the task.');
        }
        return;
      }
      if (this.capture.field === 'due') {
        const iso = this.parseDateNL(raw);
        if (iso) {
          this.taskForm.due = iso;
          this.taskDialog = true;
          this.capture = { active:false, target:null, field:null, prompt:'' };
          say('Due date set. You can say “save” to store the task.');
        } else {
          say('I did not catch the date. Try: tomorrow, next Tuesday, or in 3 days.');
        }
        return;
      }

      // If nothing matched while capturing a task, keep the dialog open and ignore
      return;
    }

    // ----- GOAL FORM VOICE FLOW -----
    if (this.capture.target === 'goal') {
      // "save" while goal dialog is open
      if (isSave) {
        if (!this.newGoal.title) {
          say('Please give the goal a title before saving.');
          this.capture = { active:true, target:'goal', field:'title', prompt:'What is the goal title?' };
          return;
        }
        // Make sure createGoal passes validation
        this.goalValid = true;
        this.createGoal();
        say('Goal saved.');
        this.capture = { active:false, target:null, field:null, prompt:'' };
        return;
      }

      let m;

      // Title
      if ((m = raw.match(/^(?:title|set\s+title(?:\s+to)?|call\s+it)\s*(?::|-)?\s*(.+)$/i))) {
        this.newGoal.title = m[1].trim();
        say('Title set.');
        // Ask next missing field in order
        if (!this.newGoal.due) {
          this.capture = { active:true, target:'goal', field:'due', prompt:'What is your target date?' };
          this.speak('What is your target date?');
        }
        return;
      }

      // Due (natural)
      if ((m = raw.match(/^(?:due|set\s+due(?:\s+to)?|by|on)\s+(.+)$/i))) {
        const iso = this.parseDateNL(m[1].trim());
        if (iso) {
          this.newGoal.due = iso;
          say('Target date set.');
          if (!this.newGoal.category) {
            this.capture = { active:true, target:'goal', field:'category', prompt:'Which category? For example fitness, learning, career or finance.' };
            this.speak('Which category? For example fitness, learning, career or finance.');
          }
        } else {
          say('I did not catch the date. Try: next Tuesday, tomorrow, or September fifteenth.');
        }
        return;
      }

      // Category
      if ((m = raw.match(/^(?:category|set\s+category(?:\s+to)?)\s*(?::|-)?\s*([A-Za-z]+)/i))) {
        this.newGoal.category = this._normalizeCategory(m[1]);
        say(`Category set to ${this.newGoal.category}.`);
        if (!this.newGoal.priority) {
          this.capture = { active:true, target:'goal', field:'priority', prompt:'What priority? Low, Medium or High.' };
          this.speak('What priority? Low, Medium or High.');
        }
        return;
      }

      // Priority
      if ((m = raw.match(/^(?:priority|set\s+priority(?:\s+to)?)\s*(?::|-)?\s*(low|medium|high)/i))) {
        const p = m[1].toLowerCase();
        this.newGoal.priority = p.charAt(0).toUpperCase() + p.slice(1);
        say(`Priority set to ${this.newGoal.priority}.`);
        if (!this.newGoal.outcome) {
          this.capture = { active:true, target:'goal', field:'outcome', prompt:'Why does this matter? Give me a quick outcome.' };
          this.speak('Why does this matter? Give me a quick outcome.');
        }
        return;
      }

      // Outcome (also accept "because …" / "so that …")
      if ((m = raw.match(/^(?:outcome|set\s+outcome(?:\s+to)?|because|so\s+that)\s*(?::|-)?\s*(.+)$/i))) {
        this.newGoal.outcome = m[1].trim();
        say('Outcome noted.');
        return;
      }

      // Fallback freeform per current field
      if (this.capture.field === 'title') {
        this.newGoal.title = raw;
        say('Title set.');
        if (!this.newGoal.due) {
          this.capture = { active:true, target:'goal', field:'due', prompt:'What is your target date?' };
          this.speak('What is your target date?');
        } else {
          this.goalDialog = true;
        }
        return;
      }
      if (this.capture.field === 'due') {
        const iso = this.parseDateNL(raw);
        if (iso) { this.newGoal.due = iso; say('Target date set.'); }
        else { say('I did not catch the date. Try: next Tuesday or in two weeks.'); }
        return;
      }
      if (this.capture.field === 'category') {
        this.newGoal.category = this._normalizeCategory(raw);
        say(`Category set to ${this.newGoal.category}.`);
        return;
      }
      if (this.capture.field === 'priority') {
        const p = raw.match(/(low|medium|high)/i);
        if (p) {
          const val = p[1].toLowerCase();
          this.newGoal.priority = val.charAt(0).toUpperCase() + val.slice(1);
          say(`Priority set to ${this.newGoal.priority}.`);
        } else {
          say('Please say Low, Medium, or High.');
        }
        return;
      }
      if (this.capture.field === 'outcome') {
        this.newGoal.outcome = raw;
        say('Outcome noted.');
        return;
      }

      // If nothing matched while capturing a goal, keep listening
      return;
    }
  }

  // ========== Not in a capture flow: treat as commands/chat ==========
  // Quick one-off “save” if a dialog is open but capture is no longer active
  if (isSave) {
    if (this.taskDialog) {
      if (!this.taskForm.title) { say('Please give the task a title before saving.'); return; }
      this.saveTask(); say('Task saved.'); return;
    }
    if (this.goalDialog) {
      if (!this.newGoal.title) { say('Please give the goal a title before saving.'); return; }
      this.goalValid = true; // ensure createGoal passes
      this.createGoal(); say('Goal saved.'); return;
    }
  }

  // Otherwise commands/chat
  if (this.handleVoiceCommand(text)) return;
  this.respondToUtterance(text);
  this.avatarCommand = { id: Date.now(), type: 'celebrate-task', payload: { xp: 15, msg: 'Task done!' } };

},


    /* ========================= INTAKE ========================= */
    saveIntake() {
      if (!this.$refs.intakeForm || !this.intakeValid) return;
      this.showIntake = false;
      this.toast(`Great to meet you, ${this.profile.name}!`);
      this.saveState();
    },

    /* ========================= GOALS ========================= */
    promptNewGoal() {
      this.newGoal = { title: '', outcome: '', category: 'Fitness', priority: 'Medium', due: null };
      this.goalDialog = true;
    },
createGoal() {
  const ok = this.$refs.goalForm ? this.$refs.goalForm.validate() : !!this.newGoal.title;
  if (!ok) return;
      const g = {
        id: 'g' + Math.random().toString(36).slice(2, 9),
        title: this.newGoal.title.trim(),
        outcome: (this.newGoal.outcome || '').trim(),
        category: this.newGoal.category,
        priority: this.newGoal.priority,
        due: this.newGoal.due,
        status: 'active',
        createdAt: new Date().toISOString(),
        path: { milestones: [] },
      };
      this.goals.unshift(g);
      this.goalDialog = false;
      this.selectGoal(g);
      this.saveState();
      this.toast('Goal created. Generate a pathway to begin.');
    },
    selectGoal(g) { this.activeGoal = g; this.saveState(); },
    deleteGoal(id) {
      const i = this.goals.findIndex(x => x.id === id);
      if (i >= 0) this.goals.splice(i, 1);
      if (this.activeGoal && this.activeGoal.id === id) this.activeGoal = this.goals[0] || null;
      this.saveState();
    },

   /* =============== AI PATHWAY (GPT or fallback) =============== */
    async generatePathway(goal) {
      this.planningId = goal.id;
      try {
        const prompt = `You are a friendly, practical personal coach. Create a short, realistic pathway for this personal goal as structured JSON.

User: ${this.profile.name || 'User'}
Focus area: ${this.profile.focusArea || 'General'}
Daily time: ${this.profile.dailyTime || '25 min'}
Constraints: ${this.profile.context || 'None'}

Goal: ${goal.title}
Desired outcome: ${goal.outcome || '(not specified)'}
Target date: ${goal.due || '(not set)'}

Return JSON with: {
  "milestones":[{"id","title","duration","when","tasks":[{"id","title","detail"}]}]
}`;

        let plan = null;
        try {
          const jsonText = await this.chatComplete([
            { role: 'system', content: 'Return only valid JSON. No prose.' },
            { role: 'user', content: prompt }
          ]);
          plan = this.safeParse(jsonText);
} catch (err) {
  if (process.env.NODE_ENV !== 'production') {
    console.debug('Ignored error:', err);
  }
}


        if (!plan || !Array.isArray(plan.milestones)) {
          plan = this.heuristicPlan(goal);
        }

        // Normalize tasks
        plan.milestones.forEach((m, mi) => {
          m.id = m.id || `m${mi+1}`;
          m.tasks = (m.tasks || []).map((t, ti) => ({
            id: t.id || `t${mi+1}-${ti+1}`,
            title: t.title || 'Unnamed task',
            detail: t.detail || '',
            done: false,
            show: false,
            note: ''
          }));
        });

        goal.path = plan;
        this.selectGoal(goal);
        this.saveState();
        this.toast('Pathway ready. Let’s crush it!');
      } finally {
        this.planningId = null;
      }
    },

    heuristicPlan(goal) {
      return {
        milestones: [
          { title: 'Kickoff & Setup', duration: '1–2 days', when: 'Today', tasks: [
            { title: `Define why "${goal.title}" matters`, detail: 'Write 3 reasons and a one-line mantra.' },
            { title: 'Set calendar blocks', detail: `Add daily ${this.profile.dailyTime || '25 min'} focus blocks.` },
            { title: 'Prepare tools', detail: 'Gather apps, gear, or materials.' }
          ]},
          { title: 'Momentum', duration: '1–2 weeks', when: 'This week', tasks: [
            { title: 'Daily tiny step', detail: 'Complete the smallest meaningful action daily.' },
            { title: 'Track progress', detail: 'Log each session; note obstacles.' },
            { title: 'Weekly review', detail: 'Reflect on what worked; adjust.' }
          ]},
          { title: 'Finish Line', duration: 'Final days', when: 'Target week', tasks: [
            { title: 'Rehearse/Simulate', detail: 'Do a dry run or mock session.' },
            { title: 'Final push', detail: 'Focus on remaining gaps only.' },
            { title: 'Celebrate & recap', detail: 'Write a short win report and next steps.' }
          ]}
        ]
      };
    },


    /* ========================= TASKS ========================= */
    expandTask(mi, ti) {
      if (!this.activeGoal?.path?.milestones?.[mi]?.tasks?.[ti]) return;
      const t = this.activeGoal.path.milestones[mi].tasks[ti];
      t.show = !t.show; this.saveState();
    },
    startTask(mi, ti) {
      if (!this.activeGoal?.path?.milestones?.[mi]?.tasks?.[ti]) return;
      const t = this.activeGoal.path.milestones[mi].tasks[ti];
      this.speak(`Starting: ${t.title}`);
    },
toggleTask(mi, ti) {
  if (!this.activeGoal?.path?.milestones?.[mi]?.tasks?.[ti]) return;
  const t = this.activeGoal.path.milestones[mi].tasks[ti];
  t.done = !t.done;
  if (t.done) {
    this.grantXP(25);
    this.beep();
    this.avatarRef()?.celebrateStep(25, 'Step complete!'); // <- ✅ add this
  }
  this.saveState();
},
    async askCoachFor(task) {
      const style = this.profile.preferredStyle || 'Supportive & encouraging';
      const q = `Give a short ${style.toLowerCase()} coaching tip for this task: ${task.title}. Context: ${task.detail || 'N/A'}. Limit to 2 sentences.`;
      try {
        const tip = await this.chatComplete([
          { role: 'system', content: 'Be concise and motivating.' },
          { role: 'user', content: q }
        ]);
        task.detail = (task.detail ? task.detail + '<br><br>' : '') + `<em>${this.escapeHTML(tip)}</em>`;
        task.show = true; this.saveState();
      } catch (_) {
        this.toast('Couldn’t fetch a tip right now.');
      }
    },
    addNote(mi, ti) {
      if (!this.activeGoal?.path?.milestones?.[mi]?.tasks?.[ti]) return;
      const t = this.activeGoal.path.milestones[mi].tasks[ti];
      const note = prompt('Add a quick note');
      if (note) { t.note = note; this.saveState(); }
    },

    /* ======================= COMPLETE GOAL ======================= */
completeGoal() {
  if (!this.isPathComplete) return;
  this.activeGoal.status = 'completed';
  this.grantXP(200);
  this.avatarRef()?.celebrateGoal(200, 'Goal completed!'); // avatar FX
  this.celebrate(); // your full-screen confetti overlay
  this.saveState();
},

    /* ======================= GAMIFICATION ======================= */
    grantXP(n) {
      this.xp += n;
      const nextLevelAt = this.level * 500;
      if (this.xp >= nextLevelAt) {
        this.level += 1;
        this.toast(`Level up! Welcome to level ${this.level}.`);
      }
      this.saveState();
    },
dailyCheckIn() {
  const today = new Date().toDateString();
  if (this.lastCheckIn === today) {
    this.toast('Already checked in today.');
    return;
  }
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  this.streak = (this.lastCheckIn === yesterday) ? this.streak + 1 : 1;
  this.lastCheckIn = today;
  this.grantXP(15);
  this.toast(`Streak ${this.streak}! Keep it going.`);
  this.avatarRef()?.celebrateStep(15, 'Daily check-in!'); // <- ✅ optional
  this.saveState();
},

    /* ======================= CELEBRATION ======================= */
    celebrate() {
      this.showCelebrate = true;
      this.$nextTick(() => this.runConfetti());
      this.speak('Amazing! Goal completed. I am proud of you.');
    },
    endCelebration() { this.showCelebrate = false; this.stopConfetti(); },

    runConfetti() {
      const cvs = this.$refs.confetti; if (!cvs) return;
      const ctx = cvs.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const resize = () => {
        cvs.width = window.innerWidth * dpr;
        cvs.height = window.innerHeight * dpr;
        cvs.style.width = window.innerWidth + 'px';
        cvs.style.height = window.innerHeight + 'px';
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };
      resize();
      this._confettiResize = resize;
      window.addEventListener('resize', resize);

      const N = 140;
      const parts = Array.from({ length: N }, () => ({
        x: Math.random() * window.innerWidth,
        y: -20 - Math.random() * 200,
        vx: -1 + Math.random() * 2,
        vy: 2 + Math.random() * 3,
        rot: Math.random() * Math.PI,
        vr: -0.1 + Math.random() * 0.2,
        w: 8 + Math.random() * 6,
        h: 10 + Math.random() * 20,
        a: 0.7 + Math.random() * 0.3
      }));

      const colors = ['#7c6cff', '#00e0b8', '#ffb703', '#28d07f', '#ff4d6d'];
      const tick = () => {
        ctx.clearRect(0, 0, cvs.width, cvs.height);
        parts.forEach(p => {
          p.x += p.vx; p.y += p.vy; p.vy += 0.03; p.rot += p.vr;
          if (p.y > window.innerHeight + 40) { p.y = -20; p.x = Math.random() * window.innerWidth; p.vy = 2 + Math.random()*3; }
          ctx.save();
          ctx.translate(p.x, p.y); ctx.rotate(p.rot);
          ctx.globalAlpha = p.a;
          ctx.fillStyle = colors[(p.h|0) % colors.length];
          ctx.fillRect(-p.w/2, -p.h/2, p.w, p.h);
          ctx.restore();
        });
        this._confettiRAF = requestAnimationFrame(tick);
      };
      this._confettiRAF = requestAnimationFrame(tick);
    },
    stopConfetti() {
      if (this._confettiRAF) cancelAnimationFrame(this._confettiRAF);
      this._confettiRAF = 0;
      window.removeEventListener('resize', this._confettiResize || (()=>{}));
      this._confettiResize = null;
    },
    /* ======================= VOICE (lite) ======================= */
 setupSpeech() {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.voiceSupported = !!SR;
      if (!SR) return;
      const rec = new SR();
      rec.continuous = true; rec.interimResults = false; rec.lang = 'en-GB';
      rec.onresult = (ev) => {
        const idx = ev.results.length - 1;
        const transcript = (ev.results[idx]?.[0]?.transcript || '').trim();
        if (!transcript) return;
        this.onTranscript(transcript);
      };
      rec.onerror = () => { this.recognizing = false; this.stopMicStream(); };
      rec.onend = () => {
        // auto-restart only if we expect to keep listening and not currently speaking
        if (this.recognizing && !this.ttsSpeaking) {
try {
  rec.start();
} catch (_) {
  void 0; // intentional no-op
}

        }
      };
      rec.onerror = () => { this.recognizing = false; };
      this.recognition = rec;
    },
async toggleVoice() {
  if (!this.voiceSupported) return;
  if (this.recognizing) {
    try { this.recognition.stop(); } catch (_) { void 0; }
    this.recognizing = false;
    this.stopMicStream();
    return;
  }
  try {
    await this.ensureAudioCtx();
    await this.startMicStream(); // start visualizer mic
    this.recognition.start();
    this.recognizing = true;
  } catch(_) { void 0; }
},
  /* ====== TASKS ====== */
openTaskDialog(task=null) {
  if (task) {
    this.taskForm = { ...task };
  } else {
    this.taskForm = { id: null, title: '', due: null, notes: '', status: 'todo' };
  }
  this.taskDialog = true;
  this.taskValid = !!this.taskForm.title;
},

saveTask() {
  const f = { ...this.taskForm };
  if (!f.title) { this.toast('Title required'); return; }
  if (f.id) {
    const i = this.quickTasks.findIndex(x => x.id === f.id);
    if (i >= 0) this.quickTasks.splice(i, 1, f);
  } else {
    f.id = 't' + Math.random().toString(36).slice(2,9);
    this.quickTasks.unshift(f);
  }
  this.taskDialog = false;
  this.capture = { active:false, target:null, field:null, prompt:'' };
  this.saveState();
},

deleteQuickTask(id) {
  const i = this.quickTasks.findIndex(x => x.id === id);
  if (i >= 0) this.quickTasks.splice(i, 1);
  this.saveState();
},

startQuickTask(t) {
  t.status = 'active';
  this.speak(`Starting: ${t.title}`);
  this.saveState();
},

completeQuickTask(t) {
  t.status = 'done';
  this.grantXP(15);
  this.avatarRef()?.celebrateTask(15, 'Task done!');
  this.saveState();
},

chipColor(st) {
  return st==='done' ? 'green' : (st==='active' ? 'deep-purple' : 'grey');
},

formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' });
},

// Parse utterance like: "add task to review business plan by September 15th, and respond to client"
parseTaskUtterance(s) {
  const lower = s.toLowerCase();
  if (!/(^|\s)(add|start|create)\s+task\b/.test(lower)) return null;

  // Remove the command phrase
  let body = s.replace(/(^|\s)(add|start|create)\s+task\b/i, '').trim();

  // Due date: "by|due|on <phrase>"
  const dueMatch = body.match(/\b(?:by|due|on)\s+([^,;.]+)(?:[.,;]|$)/i);
  let due = null;
  if (dueMatch && dueMatch[1]) {
    const iso = this.parseDateNL(dueMatch[1].trim());
    if (iso) due = iso;
    body = body.replace(dueMatch[0], '').trim();
  }

  // Optional trailing "and ..."
  let notes = '';
  const notesMatch = body.match(/\band\s+(.+)$/i);
  if (notesMatch) {
    notes = notesMatch[1].trim();
    body = body.replace(/\band\s+(.+)$/i, '').trim();
  }

  // Clean leading "to "
  body = body.replace(/^to\s+/i, '').trim();
  const title = body;

  const status = /\bstart\s+task\b/i.test(s) ? 'active' : 'todo';
  return { title, due, notes, status };
},
parseGoalUtterance(s) {
  // supports: "start goal …", "start a goal …", "start new goal …", "start a new goal …", "create goal …", "add goal …"
  if (!/^(start|create|add)\s+(?:a\s+)?(?:new\s+)?goal\b/i.test(s.trim())) return null;
  let body = s.replace(/^(start|create|add)\s+(?:a\s+)?(?:new\s+)?goal\b/i, '').trim();

  // due
  const dueMatch = body.match(/\b(?:by|due|on)\s+([^,;.]+)(?:[.,;]|$)/i);
  let due = null;
  if (dueMatch && dueMatch[1]) {
    const iso = this.parseDateNL(dueMatch[1].trim());
    if (iso) due = iso;
    body = body.replace(dueMatch[0], '').trim();
  }

  // priority
  let priority = null;
  const prMatch = body.match(/\bpriority\s*[:]?\s*(low|medium|high)\b/i);
  if (prMatch) { priority = prMatch[1][0].toUpperCase() + prMatch[1].slice(1).toLowerCase(); body = body.replace(prMatch[0], '').trim(); }

  // category
  let category = null;
  const catMatch = body.match(/\bcategory\s*[:]?\s*([A-Za-z]+)\b/i);
  if (catMatch) { category = this._normalizeCategory(catMatch[1]); body = body.replace(catMatch[0], '').trim(); }

  // outcome ("because ..." or "so that ...")
  let outcome = '';
  const outMatch = body.match(/\b(?:because|so that)\s+(.+)$/i);
  if (outMatch) { outcome = outMatch[1].trim(); body = body.replace(outMatch[0], '').trim(); }

  // title (clean leading "to ")
  body = body.replace(/^to\s+/i, '').trim();
  const title = body;

  return { title, due, category, priority, outcome };
},

_normalizeCategory(x) {
  if (!x) return null;
  const guess = x.toLowerCase();
  const match = this.goalCategories.find(c => c.toLowerCase() === guess);
  return match || (this.goalCategories[0] || 'General');
},


// Start a guided capture if some fields missing
beginVoiceCaptureForTask(prefill) {
  this.openTaskDialog();
  Object.assign(this.taskForm, prefill);

  const ask = (field, prompt) => {
    this.capture = { active:true, target:'task', field, prompt };
    this.speak(prompt);
  };

  if (!this.taskForm.title) {
    ask('title', 'What is the task title?');
  } else if (!this.taskForm.due) {
    ask('due', 'When is it due? You can say a date like September 15th or next Tuesday.');
  } else {
    this.taskDialog = true;
  }
},
beginVoiceCaptureForGoal(prefill = {}) {
  // Open the form and prefill any parsed fields
  this.newGoal = {
    title: prefill.title || '',
    outcome: prefill.outcome || '',
    category: prefill.category || (this.goalCategories?.[0] || 'Fitness'),
    priority: prefill.priority || 'Medium',
    due: prefill.due || null,
  };
  this.goalDialog = true;

  const ask = (field, prompt) => {
    this.capture = { active:true, target:'goal', field, prompt };
    this.speak(prompt);
  };

  // Ask step-by-step for any missing required/important fields
  if (!this.newGoal.title)      return ask('title', 'What is the goal title?');
  if (!this.newGoal.due)        return ask('due', 'What is your target date? You can say things like tomorrow, next Tuesday, or September fifteenth.');
  if (!this.newGoal.category)   return ask('category', 'Which category? For example fitness, learning, career or finance.');
  if (!this.newGoal.priority)   return ask('priority', 'What priority? Low, Medium or High.');
  if (!this.newGoal.outcome)    return ask('outcome', 'Why does this matter? Give me a quick outcome.');

  // If everything ready, submit immediately
  this.autoSaveGoalAfterCapture();
},
autoSaveGoalAfterCapture() {
  // satisfy form rule (title required)
  this.$nextTick(() => {
    // Create & set active
    this.createGoal();
    this.speak(`Created and started goal: ${this.newGoal.title}`);
  });
},

speak(text) {
  if (!('speechSynthesis' in window)) return;
  const u = new SpeechSynthesisUtterance(text);
  const v = this.getVoiceByName(this.profile.voice || 'Google UK English Female');
  if (v) u.voice = v;
  u.rate = 0.85; u.pitch = 1; u.volume = 1;
  this.ttsSpeaking = true;

  // Pause recognition while speaking to avoid "hearing" itself
  if (this.recognizing && this.recognition) {
    try { this.recognition.stop(); } catch (_) { void 0; } // ← no-op to satisfy ESLint
    this._resumeAfterSpeak = true;
    this.recognizing = false;
  }

  // Animate avatar mouth + a tiny energy bump
  const avatar = this.avatarRef();
  u.onboundary = () => {
    if (avatar && typeof avatar.bumpMouth === 'function') avatar.bumpMouth(1);
    this.waveLevel = Math.min(1, this.waveLevel + 0.08);
  };
  u.onend = () => {
    this.ttsSpeaking = false;
    if (avatar && typeof avatar.bumpMouth === 'function') avatar.bumpMouth(0);
    // Smoothly decay viz energy
    this.waveLevel = 0.15;
    // Resume recognition if we were listening before
    if (this._resumeAfterSpeak && this.voiceSupported && this.recognition) {
      this._resumeAfterSpeak = false;
      try { this.recognition.start(); this.recognizing = true; } catch (_) { void 0; } // ← fixed
    }
  };

  // Restart queue and speak
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
},

 nudge(message, mood='upbeat') {
      this.avatarCaption = message;
      this.avatarMood = mood;
      setTimeout(()=> this.avatarCaption = '', 3500);
    },
getVoiceByName(name) {
      const s = window.speechSynthesis; if (!s) return null;
      const vs = s.getVoices() || [];
      return vs.find(v => v.name === name)
          || vs.find(v => /en-GB/i.test(v.lang))
          || vs.find(v => /en-/i.test(v.lang))
          || vs[0]
          || null;
    },

 /* ===== Listen → Coach Answer (keeps your OpenAI connection) ===== */
    async respondToUtterance(text) {
      // Lightweight system prompt using profile + active goal
      const sys = `You are a concise, encouraging personal coach. Match the user's preferred style: "${this.profile.preferredStyle || 'Supportive & encouraging'}". Keep responses to 1–3 short sentences. If the user mentions their current goal, give one practical next step.`;
      const goalLine = this.activeGoal ? `Current goal: ${this.activeGoal.title}.` : '';
      const context = [
        this.profile.focusArea ? `Focus area: ${this.profile.focusArea}.` : '',
        this.profile.dailyTime ? `Daily time: ${this.profile.dailyTime}.` : '',
        this.profile.context ? `Constraints: ${this.profile.context}.` : '',
        goalLine
      ].filter(Boolean).join(' ');

      try {
        this.loading = true;
        const reply = await this.chatComplete([
          { role: 'system', content: sys },
          { role: 'user', content: `${context}\n\nUser said: ${text}` }
        ]);
        const trimmed = String(reply || '').trim();
        if (trimmed) {
          this.toast(trimmed);
          this.speak(trimmed);
          this.nudge('✅ Logged your question', 'friendly');
        }
      } catch (e) {
        this.toast('I had trouble answering that just now.');
      } finally {
        this.loading = false;
      }
    },

handleVoiceCommand(t) {
  const raw = String(t || '').trim();
  const s = raw.toLowerCase();
  const say = (m) => { this.toast(m); this.speak(m); };

  /* ========= Quick global actions (when not in capture flow) ========= */
  // Save (works if a dialog is open and we're not actively capturing a field)
  if (/^(ok(ay)?\s+)?save(\s+(it|this|that|task|goal))?$|^(done|finish(ed)?|that's it|that is it|looks good|good to go)$/i.test(s)) {
    if (!this.capture.active) {
      if (this.taskDialog) {
        if (!this.taskForm.title) { say('Please give the task a title before saving.'); return true; }
        this.saveTask(); say('Task saved.'); return true;
      }
      if (this.goalDialog) {
        if (!this.newGoal.title) { say('Please give the goal a title before saving.'); return true; }
        this.goalValid = true; // allow createGoal to pass validation
        this.createGoal(); say('Goal saved.'); return true;
      }
    }
  }

// Open Intake/Profile overlay
if (/^(open|show|edit|setup|set up)\s+(intake|profile|coach profile)$/i.test(s)) {
  this.openIntakeDialog();
  this.toast('Opening your profile.');
  this.speak('Opening your profile.');
  return true;
}


  // Cancel (close any open dialog and stop capture)
  if (/^(cancel|close|dismiss|never mind|nevermind|stop)$/i.test(s)) {
    if (this.taskDialog || this.goalDialog || this.capture.active) {
      this.taskDialog = false;
      this.goalDialog  = false;
      this.capture     = { active: false, target: null, field: null, prompt: '' };
      say('Okay, canceled.');
      return true;
    }
  }

  /* ========================= Coach features ========================= */
// Create/Start a goal (guided capture + inline parsing)
if (/^(start|create|add)\s+(?:a\s+)?(?:new\s+)?goal\b/i.test(s)) {
  const parsed = this.parseGoalUtterance(raw); // accepts optional "a"/"new"
  this.beginVoiceCaptureForGoal(parsed || {});
  this.speak(parsed?.title
    ? `Setting up goal: ${parsed.title}. I’ll fill in the form as you speak.`
    : 'Okay, let’s create a goal. What is the goal title?');
  return true;
}



  if (/generate (?:path|plan)/i.test(s) && this.activeGoal) { this.generatePathway(this.activeGoal); return true; }
  if (/(?:\bhint\b|coach me)/i.test(s)) { this.coachTip(); return true; }
  if (/(check(?:\s|-)?in|streak)/i.test(s)) { this.dailyCheckIn(); return true; }

  // Speak status / what's next
  if (/^(status|what'?s next|what is next)\b/i.test(s)) {
    this.toast(this.nextStepBrief);
    this.speak(this.nextStepBrief);
    return true;
  }

  /* ========================= Goal management ========================= */
  // Open/switch goal by title
  const openGoalMatch = s.match(/^(open|show|switch to)\s+goal\s+(.+)$/i);
  if (openGoalMatch) {
    const title = openGoalMatch[2].trim();
    const g = this.goals.find(x => x.title.toLowerCase() === title.toLowerCase());
    if (g) { this.selectGoal(g); say(`Opening goal: ${g.title}`); }
    else { say('I couldn’t find that goal.'); }
    return true;
  }

  // Natural add-goal shortcut: "goal: …" or "goal - …" → start guided capture
  const colonGoal = raw.match(/^goal\s*(?::|-)\s*(.+)$/i);
  if (colonGoal) {
    this.beginVoiceCaptureForGoal({ title: colonGoal[1].trim() });
    say('Got it. I’ll open the goal form — you can say due date, category, priority, and outcome.');
    return true;
  }

  // Complete goal (only if pathway is complete)
  if (/^complete goal\b/i.test(s)) {
    if (this.isPathComplete) { this.completeGoal(); }
    else {
      const msg = `Not quite yet — ${this.completedCount} of ${this.totalCount} steps done. ${this.nextStepBrief}`;
      this.toast(msg); this.speak(msg);
    }
    return true;
  }

  /* ========================= Pathway actions ========================= */
  // Complete pathway step (title optional; default = next step)
  const completeStepMatch = s.match(/^complete (?:step|path(?:way)? step)(?:\s+(.+))?$/i);
  if (completeStepMatch) {
    const title = (completeStepMatch[1] || '').trim();
    if (!this.activeGoal?.path) { this.toast('No active goal/pathway.'); return true; }

    let target = this.getNextPathStep();
    if (title) {
      for (let mi = 0; mi < this.activeGoal.path.milestones.length; mi++) {
        const m = this.activeGoal.path.milestones[mi];
        const ti = (m.tasks || []).findIndex(tt => !tt.done && tt.title.toLowerCase().includes(title.toLowerCase()));
        if (ti >= 0) { target = { mi, ti, title: m.tasks[ti].title }; break; }
      }
    }
    if (target) this.completeMilestoneTask(target.mi, target.ti);
    else this.toast('No matching step found.');
    return true;
  }

  /* ========================= Quick tasks ========================= */
  // Add/start/create task (inline details supported)
  if (/(^|\s)(add|start|create)\s+task\b/i.test(s)) {
    const parsed = this.parseTaskUtterance(raw);
    if (parsed) {
      const needsCapture = !parsed.title || !parsed.due;
      if (needsCapture) {
        this.beginVoiceCaptureForTask(parsed); // opens the form and asks remaining fields
      } else {
        const task = { id: 't' + Math.random().toString(36).slice(2, 9), ...parsed };
        this.quickTasks.unshift(task);
        this.toast('Task captured.');
        this.speak('Task captured. You can edit or say “save” anytime.');
        this.saveState();
      }
      return true;
    }
    // Couldn’t parse — start guided capture
    this.beginVoiceCaptureForTask({ title: '', due: null, notes: '', status: 'todo' });
    return true;
  }

  // Start task by exact title
  const startMatch = s.match(/^start task\s+(.+)$/i);
  if (startMatch) {
    const title = startMatch[1].trim();
    const found = this.quickTasks.find(q => q.title.toLowerCase() === title.toLowerCase());
    if (found) { this.startQuickTask(found); }
    else {
      const task = { id: 't' + Math.random().toString(36).slice(2, 9), title, due: null, notes: '', status: 'active' };
      this.quickTasks.unshift(task);
      this.speak(`Starting: ${title}`);
      this.saveState();
    }
    return true;
  }

  // Complete task by exact title
  const completeTaskMatch = s.match(/^complete task\s+(.+)$/i);
  if (completeTaskMatch) {
    const title = completeTaskMatch[1].trim();
    const tsk = this.quickTasks.find(q => q.title.toLowerCase() === title.toLowerCase());
    if (tsk) this.completeQuickTask(tsk);
    else this.toast('Couldn’t find that task.');
    return true;
  }

  // Edit task by title
  const editTaskMatch = s.match(/^(edit|open)\s+task\s+(.+)$/i);
  if (editTaskMatch) {
    const title = editTaskMatch[2].trim();
    const tsk = this.quickTasks.find(q => q.title.toLowerCase() === title.toLowerCase());
    if (tsk) { this.openTaskDialog(tsk); say(`Editing task: ${tsk.title}`); }
    else { this.toast('Couldn’t find that task.'); }
    return true;
  }

  // Delete task by title
  const deleteTaskMatch = s.match(/^(delete|remove)\s+task\s+(.+)$/i);
  if (deleteTaskMatch) {
    const title = deleteTaskMatch[2].trim();
    const tsk = this.quickTasks.find(q => q.title.toLowerCase() === title.toLowerCase());
    if (tsk) { this.deleteQuickTask(tsk.id); say('Task deleted.'); }
    else { this.toast('Couldn’t find that task.'); }
    return true;
  }

  // Lists / summaries
  if (/^(list|show)\s+tasks$/i.test(s)) {
    const pending = this.quickTasks.filter(x => x.status !== 'done');
    const names = pending.slice(0, 6).map(x => x.title).join(', ') || 'no pending tasks';
    say(`You have ${pending.length} pending: ${names}.`);
    return true;
  }
  if (/^(list|show)\s+goals$/i.test(s)) {
    const names = this.goals.slice(0, 6).map(x => x.title).join(', ') || 'no goals yet';
    say(`You have ${this.goals.length} goals: ${names}.`);
    return true;
  }

  // Nothing matched — let normal chat flow handle it
  return false;
},


/* ====== VISUALIZER ====== */
initVoiceViz() {
  const cvs = this.$refs.vizCanvas; if (!cvs) return;
  const ctx = cvs.getContext('2d'); this.viz.ctx = ctx;

  const resize = () => {
    const dpr = this.viz.dpr || 1;
    const pxW = Math.min(1200, Math.floor(cvs.parentElement?.clientWidth || 760));
    const pxH = 160;
    cvs.width = pxW * dpr; cvs.height = pxH * dpr;
    cvs.style.width = pxW + 'px'; cvs.style.height = pxH + 'px';
    ctx.setTransform(dpr,0,0,dpr,0,0);
  };
    this._vizResize = resize;
  window.addEventListener('resize', this._vizResize);
  resize(); window.addEventListener('resize', resize);

  const loop = () => { this.renderVizModern(); this.viz.raf = requestAnimationFrame(loop); };
  loop();
},
teardownViz() {
  if (this.viz.raf) cancelAnimationFrame(this.viz.raf);
  this.viz.raf = 0;
},
async ensureAudioCtx() {
  if (!this.viz.audioCtx) {
    try { this.viz.audioCtx = new (window.AudioContext || window.webkitAudioContext)(); }
    catch (_) { /* no-op */ }
  }
  if (this.viz.audioCtx && this.viz.audioCtx.state === 'suspended') {
    try { await this.viz.audioCtx.resume(); } catch (_) { /* no-op */ }
  }
  return this.viz.audioCtx;
},
async startMicStream() {
  try {
    const ac = await this.ensureAudioCtx(); if (!ac) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const src = ac.createMediaStreamSource(stream);
    const analyser = ac.createAnalyser();
    analyser.fftSize = 1024; analyser.smoothingTimeConstant = 0.8;
    src.connect(analyser);
    this.viz.analyser = analyser;
    this.viz.stream = stream;
    this.viz.buf = new Uint8Array(analyser.frequencyBinCount);
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.debug('Mic stream denied/unavailable', err);
  }
},
stopMicStream() {
  try {
    this.viz.stream?.getTracks?.().forEach(t => t.stop());
  } catch (_) { /* no-op */ }
  this.viz.stream = null;
  this.viz.analyser = null;
  this.viz.buf = null;
},
renderVizModern() {
  this.waveLevel = this.viz.energy; // keeps the avatar feeling “alive”

  const cvs = this.$refs.vizCanvas; const ctx = this.viz.ctx; if (!cvs || !ctx) return;
  const W = cvs.width / (this.viz.dpr||1), H = cvs.height / (this.viz.dpr||1);
  ctx.clearRect(0,0,W,H);

  let energy = 0.18; // idle baseline
  if (this.viz.analyser && this.viz.buf) {
    this.viz.analyser.getByteFrequencyData(this.viz.buf);
    // average lower bands for “voice energy”
    const N = Math.min(64, this.viz.buf.length);
    let sum = 0; for (let i=0;i<N;i++) sum += this.viz.buf[i];
    energy = Math.max(0.12, sum / (N*255));
  } else if (this.ttsSpeaking || this.recognizing) {
    // animated if listening/speaking without mic graph
    energy = this.ttsSpeaking ? 0.6 : 0.35;
  }

  // Smooth energy
  this.viz.energy = this.viz.energy*0.8 + energy*0.2;
  const e = this.viz.energy;

  // Background gradient glow
  const grd = ctx.createLinearGradient(0,0,W,H);
  grd.addColorStop(0, 'rgba(141,121,255,0.18)');
  grd.addColorStop(1, 'rgba(0,245,200,0.12)');
  ctx.fillStyle = grd; ctx.fillRect(0,0,W,H);

  // Bars
  const bars = 48;
  const bw = W / bars;
  const t = performance.now()/650;
  for (let i=0;i<bars;i++){
    let val = e; // base
    if (this.viz.buf) {
      const idx = Math.floor((i / bars) * (this.viz.buf.length-1));
      val = Math.max(e*0.8, this.viz.buf[idx]/255);
    } else {
      // idle wave
      val += 0.15*Math.sin(i*0.5 + t*4);
    }
    const h = Math.max(8, val * (H*0.9));
    const x = i*bw + 2, y = H - h;
    const rad = 8;
    ctx.fillStyle = 'rgba(141,121,255,0.75)';
    ctx.beginPath();
    ctx.moveTo(x, y+rad);
    ctx.arcTo(x, y, x+rad, y, rad);
    ctx.lineTo(x+bw-4-rad, y);
    ctx.arcTo(x+bw-4, y, x+bw-4, y+rad, rad);
    ctx.lineTo(x+bw-4, H-rad);
    ctx.arcTo(x+bw-4, H, x+bw-4-rad, H, rad);
    ctx.lineTo(x+rad, H);
    ctx.arcTo(x, H, x, H-rad, rad);
    ctx.closePath();
    ctx.fill();
  }

  // Orb
  const orbX = W*0.12 + Math.sin(t*1.8)*W*0.04;
  const orbY = H*0.45 + Math.cos(t*1.4)*H*0.06;
  const r = 18 + e*36;
  const g = ctx.createRadialGradient(orbX,orbY, r*0.1, orbX,orbY, r);
  g.addColorStop(0, 'rgba(0,245,200,0.9)');
  g.addColorStop(1, 'rgba(0,245,200,0.0)');
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(orbX,orbY,r,0,Math.PI*2); ctx.fill();
},
    renderViz() {
      const cvs = this.$refs.vizCanvas; const ctx = this.viz.ctx; if (!cvs||!ctx) return;
      const W = cvs.width / (this.viz.dpr||1); const H = cvs.height / (this.viz.dpr||1);
      ctx.clearRect(0,0,W,H);
      const e = this.ttsSpeaking ? 0.6 : (this.recognizing ? 0.9 : 0.15);
      const y = H/2; const amp = 40*e; const t = performance.now()/650;
      ctx.beginPath(); ctx.moveTo(0,y);
      for (let x=0; x<=W; x+=3) {
        const yy = y + Math.sin(x/30 + t*4)*amp + Math.sin(x/9 + t*9)*(amp*0.18);
        ctx.lineTo(x, yy);
      }
      ctx.strokeStyle = 'rgba(124,108,255,0.95)'; ctx.lineWidth = 2 + e*2; ctx.shadowColor = 'rgba(124,108,255,0.6)'; ctx.shadowBlur = 10 + e*12; ctx.stroke();
    },

    /* ======================= GPT HELPER ======================= */

    /* ======================= GPT HELPER ======================= */
    async chatComplete(messages) {
      if (!this.gptApiKey) throw new Error('Missing OpenAI key');
      const res = await axios.post('https://api.openai.com/v1/chat/completions', {
        model: this.gptModel, messages, temperature: 0.4, max_tokens: 1200
      }, { headers: { Authorization: `Bearer ${this.gptApiKey}` } });
      return String(res.data?.choices?.[0]?.message?.content || '').trim();
    },

    /* ======================= UTIL ======================= */
    safeParse(s) { try { return JSON.parse(s); } catch(_) { return null; } },
    escapeHTML(s='') {
      return s.replace(/[&<>"']/g, ch => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[ch]));
    },
    toast(m) { this.snack.msg = m; this.snack.on = true; },
  onKey(e) {
  const isSpace = e.code === 'Space' || e.key === ' ';
  if (!isSpace || e.repeat) return;
      if (e.code !== 'Space' || e.repeat) return;
      const tag = (e.target && e.target.tagName) ? e.target.tagName.toLowerCase() : '';
      const editable = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
      if (editable) return;
      e.preventDefault();
      this.toggleVoice();
    },
    beep() {
      try {
        const ac = new (window.AudioContext||window.webkitAudioContext)();
        const o = ac.createOscillator(); const g = ac.createGain();
        o.type='triangle'; o.frequency.value=880; o.connect(g); g.connect(ac.destination);
        g.gain.setValueAtTime(0.0001, ac.currentTime);
        g.gain.exponentialRampToValueAtTime(0.2, ac.currentTime+0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime+0.25);
        o.start(); o.stop(ac.currentTime+0.3);
} catch (_) {
  void 0; // intentional no-op
}

    },

    /* ======================= COACH TIP ======================= */
    async coachTip() {
      if (!this.activeGoal) { this.toast('No active goal.'); return; }
      const next = this.findNextTask();
      const q = next ? `User is working on: ${next.title}. Provide one short, energetic tip.` : `Provide a short motivation aligned to ${this.profile.focusArea || 'personal growth'}.`;
      try {
        const tip = await this.chatComplete([
          { role: 'system', content: 'Be concise, 1–2 sentences, positive and practical.' },
          { role: 'user', content: q }
        ]);
        this.toast(tip);
        this.speak(tip);
      } catch(_) {
        this.toast('Here’s a tip: start tiny, finish happy.');
      }
    },
 findNextTask() {
      if (!this.activeGoal?.path) return null;
      for (const m of this.activeGoal.path.milestones) {
        const t = (m.tasks||[]).find(x => !x.done);
        if (t) return t;
      }
      return null;
    },


    /* ======================= PERSIST ======================= */
saveState() {
  const state = {
    profile: this.profile,
    goals: this.goals,
    activeGoalId: this.activeGoal?.id || null,
    xp: this.xp, level: this.level, streak: this.streak, lastCheckIn: this.lastCheckIn,
    isDark: this.isDark,
    quickTasks: this.quickTasks, // NEW
  };
  try { localStorage.setItem('coach.state.v1', JSON.stringify(state)); } catch (_) { void 0; }
},
loadState() {
  try {
    const raw = localStorage.getItem('coach.state.v1'); if (!raw) return;
    const s = JSON.parse(raw);
    this.profile = s.profile || this.profile;
    this.goals = s.goals || [];
    this.activeGoal = this.goals.find(g => g.id === s.activeGoalId) || this.goals[0] || null;
    this.xp = s.xp || 0; this.level = s.level || 1; this.streak = s.streak || 0; this.lastCheckIn = s.lastCheckIn || null;
    this.isDark = !!s.isDark;
    this.quickTasks = s.quickTasks || []; // NEW
  } catch (_) { void 0; }
},

  }
};
</script>


<style scoped>
.status-card .status-counters {
  display: flex; gap:30px; flex-wrap: wrap; margin-bottom: 20px;
}
.status-card .status-brief {
  font-size: 13px; color: #a9afd2;
}

.tasks-card .task-list { display:flex; flex-direction:column; gap:10px; }
.qtask{
  display:grid; grid-template-columns: 1fr auto; gap:8px;
  border:1px solid var(--border); border-radius:12px; padding:10px;
  background: rgba(255,255,255,0.04);
}
.qtask.active{ outline:2px solid rgba(141,121,255,.25); }
.qtask.done{ opacity:.7; }
.qtask-main .qtask-title{ font-weight:800; }
.qtask-sub{ font-size:12px; }
.qtask-meta{ display:flex; align-items:center; }
.qtask-actions{ grid-column: 1 / -1; display:flex; gap:6px; flex-wrap:wrap; }

/* === Main grid with template areas === */
.grid-areas{
  display: grid;
  grid-template-columns: 1fr 1.15fr 1fr;
  grid-template-areas:
    "left center right";
  gap: clamp(12px, 1.6vw, 18px);
  align-content: start;
  min-height: 0; /* prevents overflow issues */
}

/* Area hooks */
.col-left   { grid-area: left;  }
.col-center { grid-area: center; display: grid; grid-auto-rows: min-content; gap: clamp(10px, 1.2vw, 14px); }
.col-right  { grid-area: right; }

/* Make the center “voice card” compact */
.center-card { padding: clamp(12px, 1.2vw, 16px); }

/* Desktop tweaks: give center column a subtle emphasis */
@media (min-width: 1021px){
  .col-center .card.neon {
    border-color: rgba(0,245,200,.25);
    box-shadow:
      0 10px 40px rgba(0,0,0,.45),
      0 0 0 1px rgba(0,245,200,.16),
      inset 0 0 50px rgba(0,245,200,.06);
  }
}

/* Collapse to single column on mobile */
@media (max-width: 1020px){
  .grid-areas{
    grid-template-columns: 1fr;
    grid-template-areas:
      "center"
      "left"
      "right";
  }
}

/* ========= Global / page canvas ========= */
:root{
  --bg-1:#0b1022; --bg-2:#101739; --bg-3:#0e1b2e;
  --panel:#11172e;
  --text:#eaf0ff; --muted:#a9b4e3;
  --accent:#8d79ff; --accent-2:#00f5c8; --accent-3:#ffb703;
  --ok:#28d07f; --danger:#ff4d6d;
  --card: rgba(255,255,255,0.06);
  --border: rgba(255,255,255,0.16);
  --border-strong: rgba(255,255,255,0.22);
  --shadow: 0 10px 40px rgba(0,0,0,.45);
}

html, body, #app, .v-application, .bg-content-coach {
  min-height: 100vh;
  height: 100%;
}

.bg-content-coach {
  /* animated gradient backdrop */
  background: linear-gradient(-45deg,  #0b654d, #c9415a, #000417);
  padding: 0px;
  min-height: 100%;
  -webkit-animation: gradient 25s ease infinite;
  background-size: 200% 200%;
  animation: gradient 25s bgShift 28s ease-in-out infinite;
}
@-webkit-keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
  height: 150%;
}
@keyframes bgShift {
  0% { background-position: 0% 40%, 100% 60%, 0% 0%; }
  50% { background-position: 60% 60%, 40% 40%, 100% 100%; }
  100% { background-position: 0% 40%, 100% 60%, 0% 0%; }
}

/* ========= App wrapper & header ========= */
.coach-wrap{
  width: 100%;
  max-width: 1900px;
  margin: 50px 50px -50px -0px;
  padding: clamp(50px, 3vw, 50px);
  display: grid;
  grid-template-rows: auto auto auto auto 1fr;
  gap: clamp(12px, 1.6vw, 18px);
}
.avatar{
  position:relative; display:flex; align-items:center; gap:30px; margin-left: -80px;
}
.coach-header{
  position: sticky;
  top: 0;
  z-index: 10;
  display:flex; align-items:center; justify-content:space-between;
  padding: 12px clamp(12px, 2vw, 18px);
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04));
  border: 1px solid var(--border);
  color: var(--text);
  backdrop-filter: blur(10px) saturate(130%);
  box-shadow: var(--shadow);
}

.brand{ display:flex; align-items:center; gap:12px; }
.brand-mark{
  width:44px; height:44px; border-radius:14px;
  background: conic-gradient(from 210deg, var(--accent), var(--accent-2), var(--accent));
  box-shadow: 0 8px 30px rgba(0,0,0,.35), 0 0 40px rgba(141,121,255,.25);
}
.title{ font-size: clamp(18px, 1.6vw, 24px); font-weight:800; color:var(--text); letter-spacing:.2px; }
.subtitle{ font-size: clamp(11px, .95vw, 13px); color:var(--muted); }

.right-tools{ display:flex; align-items:center; gap:10px; flex-wrap: wrap; }
.points-pill, .streak-pill, .level-pill{
  display:flex; align-items:center; gap:6px; padding:6px 12px;
  border:1px solid var(--border);
  border-radius: 999px;
  background: rgba(255,255,255,0.06);
  color: var(--text);
  backdrop-filter: blur(8px) saturate(120%);
}
.points-pill strong, .streak-pill strong, .level-pill strong { font-weight:800; }

/* ========= Voice row ========= */
.voice-row{
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(20px, 1.8vw, 20px);
  align-items:center;
}

@media (max-width: 1020px){
  .voice-row{ grid-template-columns: 1fr; }
}

.avatar-container{ position:relative; display:flex; align-items:center; gap:90px; margin-left: 80px; }
.pulse-glow{
  position:absolute; left:25px; top:25px; width:150px; height:150px; border-radius:50%;
  background: radial-gradient(circle at 50% 50%, rgba(141,121,255,.35), rgba(141,121,255,0));
  filter:blur(10px); opacity:.6\8;
}
.mic-wrap{ position:relative; margin-left: -10px; }
.mic-btn{
  width:44px; height:44px; border-radius:999px;
  border:1px solid var(--border-strong);
  background: rgba(255,255,255,0.08);
  color: var(--text); box-shadow: var(--shadow);
  transition: transform .15s ease;
}
.mic-btn:hover{ transform: translateY(-1px) scale(1.02); }
.mic-btn.on{ outline:2px solid rgba(40,208,127,.5); box-shadow: 0 0 0 6px rgba(40,208,127,.12); }

.voice-ring{
  position:absolute; inset:-10px; border-radius:999px;
  border:2px solid rgba(141,121,255,.55);
  box-shadow: 0 0 18px rgba(141,121,255,.35);
  opacity:.35; transform:scale(.9);
  transition:transform .25s ease, opacity .25s ease;
}
.voice-ring.on{ opacity:.9; transform:scale(1); }

.voice-viz{
  display:block; width:100%; height:180px; border-radius:16px;
  background: linear-gradient(120deg,#2931672b,#12162c13);
  border:1px solid var(--border);
  margin-left: -60px;
  margin-right: 90px;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.05), var(--shadow);
}

.voice-cta-wrap{ display:flex; align-items:center; gap:35px; flex-wrap: wrap; }
.voice-hint{ color:var(--muted); }
.voice-hint.warn{ color:#ffb703; }

/* ========= Transparent “glass” cards ========= */
.card{
  background: rgba(255,255,255,0.06);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: clamp(14px, 1.6vw, 18px);
  color: var(--text);
  box-shadow: var(--shadow);
  backdrop-filter: blur(10px) saturate(130%);
}
.neon{
  box-shadow:
    0 10px 40px rgba(0,0,0,.45),
    0 0 0 1px rgba(141,121,255,.18),
    inset 0 0 50px rgba(141,121,255,.08);
}

/* ========= Input & transcript rows ========= */
.input-row{
  display:grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items:center;
}

@media (max-width: 640px){
  .input-row{ grid-template-columns: 1fr; }
  .input-row .v-btn{ width: 100%; }
}

.transcript-row{ }
.transcript-row h4{
  margin: 0 0 8px;
  font-size: clamp(14px, 1vw, 16px);
  color: var(--accent-2);
  font-weight: 700;
  letter-spacing: .2px;
}
.transcript-box{
  background: rgba(255,255,255,0.04);
  border: 1px dashed var(--border);
  border-radius: 12px;
  padding: 12px;
  min-height: 44px;
  font-size: 14px;
  color: var(--text);
}
.transcript-line{ white-space: pre-wrap; }

/* ========= Main grid (goals / pathway) ========= */
.grid{
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: clamp(12px, 1.6vw, 18px);
  align-content: start;
}

.section-head{
  display:flex; align-items:center; justify-content:space-between;
  margin-bottom:10px;
}
.section-head h3{
  margin:0;
  font-size: clamp(16px, 1.4vw, 20px);
  font-weight: 800;
  letter-spacing:.2px;
  background: linear-gradient(90deg, var(--text), rgba(255,255,255,.85));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.muted{ color:var(--muted); }

/* Goal items */
.goal-list{ display:flex; flex-direction:column; gap:10px; }
.goal-item{
  border:1px solid var(--border);
  border-radius:14px; padding:12px;
  display:grid; grid-template-columns: 1fr auto; gap:8px;
  background: rgba(255,255,255,0.04);
}
.goal-item.active{ outline:2px solid rgba(141,121,255,.35); }
.gi-main .gi-title{ font-weight:800; letter-spacing:.2px; }
.gi-main .gi-sub{ color:var(--muted); font-size:12px; margin-top:4px; }
.gi-meta{ display:flex; gap:6px; align-items:center; flex-wrap: wrap; }
.gi-actions{ display:flex; gap:8px; grid-column: 1 / -1; flex-wrap: wrap; }

/* Progress & tasks */
.progress-row{
  display:grid; grid-template-columns: 70px 1fr auto;
  align-items:center; gap:12px; margin:10px 0 14px;
}
.pr-text, .pr-num{ color:var(--muted); font-size:12px; }

.milestone{
  border:1px dashed var(--border);
  border-radius:14px; padding:12px; margin-bottom:12px;
  background: rgba(255,255,255,0.03);
}
.ms-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:6px; }
.ms-title{ font-weight:800; }
.task{
  border:1px solid rgba(255,255,255,0.08);
  border-radius:12px; padding:10px; margin:8px 0;
  background: rgba(255,255,255,0.04);
}
.task.done{ opacity:.6; }
.task-actions{ display:flex; gap:6px; flex-wrap: wrap; }
.task-details{ margin-top:6px; font-size:13px; }
.note{ margin-top:6px; color:#ffd166; }

/* ========= Switch (dark toggle) ========= */
.tog{ display:flex; align-items:center; gap:8px; color:var(--muted); }
.switch{
  width:54px; height:28px; border-radius:999px;
  background:rgba(255,255,255,0.1);
  border:1px solid var(--border); position:relative; cursor:pointer;
}
.switch::after{
  content:''; position:absolute; top:3px; left:3px;
  width:22px; height:22px; border-radius:50%; background:#fff;
  box-shadow: 0 2px 8px rgba(0,0,0,.35);
  transition: transform .22s ease, left .22s ease;
}
.switch.on{
  background:linear-gradient(90deg, rgba(0,245,200,.35), rgba(141,121,255,.35));
  border-color: rgba(0,245,200,.65);
}
.switch.on::after{ left:29px; }

/* ========= Celebrate overlay ========= */
.celebrate{
  position:fixed; inset:0; z-index:9999;
  display:flex; align-items:center; justify-content:center;
  backdrop-filter: blur(4px) saturate(120%);
}
.confetti{ position:absolute; inset:0; width:100%; height:100%; }
.celebrate-card{
  position:relative; z-index:2;
  background: rgba(255,255,255,0.07);
  border:1px solid var(--border);
  border-radius:22px; padding:28px; text-align:center;
  box-shadow: var(--shadow);
  backdrop-filter: blur(10px) saturate(130%);
}
.celebrate-card .big{ font-size: 54px; }

/* ========= Buttons (brighter) ========= */
.v-btn.primary, .v-btn--has-bg{
  font-weight: 700;
  letter-spacing: .2px;
}

/* ========= Accessibility / motion ========= */
@media (prefers-reduced-motion: reduce){
  .bg-content-coach{ animation: none; }
  .voice-ring{ transition: none; }
}

</style>